// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect, useState } from 'react';
import styles from './app.module.css';
import Nav from './features/nav/nav';

import { useAppDispatch, useAppSelector } from './app/hooks';
import { close } from './features/nav/navSlice';
import TopBar from './features/top-bar/top-bar';
import { CommandPalette } from './features/command-palette';
import { useLocation, Routes, Route } from 'react-router-dom';
import { Routes as RouteList } from './app/routes';
import { Login } from './features/login/login';
import { selectToken } from './features/login/authSlice';
import { selectUploads } from './features/uploader/uploader-slice';
import { UploadStatus } from './features/uploader/uploader.types';
import { Register } from './features/register/register';
import { Forgotten } from './features/forgotten/forgotten';
import { ResetPassword } from './features/forgotten/reset';
import { SwitchAccount } from './features/switch-account/switch-account';
import { Debug } from './features/debug/debug';
import { Coupon } from './features/coupons/coupon';
import { Enquiry } from './features/enquiries/enquiry';
import { Products } from './features/products/products';
import { Product } from './features/products/product';
import { SelectorTemplates } from './features/product-selectors/product-selectors';
import { SelectorTemplateEditor } from './features/product-selectors/product-selector-editor';
import { Coupons } from './features/coupons/coupons';
import { Orders } from './features/orders/orders';
import { Order } from './features/orders/order';
import { Events } from './features/events/events';
import { Event } from './features/events/event';
import { Album } from './features/albums/album';
import { Dashboard } from './features/dashboard/dashboard';
import { Enquiries } from './features/enquiries/enquiries';
import { Settings } from './features/settings/settings';
import { Tasks } from './features/tasks/tasks';
import { Task } from './features/tasks/task';
import { Users } from './features/users/users';
import { User } from './features/users/user';
import { Staging } from './features/staging/staging';
import { BillingBanner } from './features/settings/BillingBanner';

export function App() {
  const dispatch = useAppDispatch();
  const token = useAppSelector(selectToken);
  const location = useLocation();
  const uploads = useAppSelector(selectUploads);
  const uploadingCount = uploads.filter(
    (u) => u.status === UploadStatus.Uploading
  ).length;

  useEffect(() => {
    dispatch(close());
  }, [location, dispatch]);

  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    window.onbeforeunload = uploadingCount
      ? function () {
          return false;
        }
      : null;
  }, [uploadingCount]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  if (!token) {
    return (
      <Routes>
        <Route path={RouteList.REGISTER.link} element={<Register />} />
        <Route
          path={RouteList.RESETPASSWORD.link}
          element={<ResetPassword />}
        />
        <Route path={RouteList.FORGOTTEN.link} element={<Forgotten />} />
        <Route path={RouteList.LOGIN.link} element={<Login />} />
        <Route
          path={RouteList.SWITCHACCOUNT.link}
          element={<SwitchAccount />}
        />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  if (location.pathname === RouteList.SWITCHACCOUNT.link) {
    return (
      <Routes>
        <Route
          path={RouteList.SWITCHACCOUNT.link}
          element={<SwitchAccount />}
        />
      </Routes>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <Nav />
      <div className={styles.pageContentWrapper}>
        <BillingBanner />
        <TopBar onSearchClick={() => setPaletteOpen(true)} />
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

        <div className={styles.pageContent}>
          <Routes>
            <Route path={RouteList.DEBUG.link} element={<Debug />} />
            <Route path={RouteList.TASKS.link} element={<Tasks />} />
            <Route path={RouteList.TASK.link} element={<Task />} />
            <Route path={RouteList.USERS.link} element={<Users />} />
            <Route path={RouteList.USER.link} element={<User />} />
            <Route path={RouteList.EVENTS.link} element={<Events />} />
            <Route path={RouteList.EVENT.link} element={<Event />} />
            <Route path={RouteList.ALBUM.link} element={<Album />} />
            <Route path={RouteList.COUPONS.link} element={<Coupons />} />
            <Route path={RouteList.COUPON.link} element={<Coupon />} />
            <Route path={RouteList.ORDERS.link} element={<Orders />} />
            <Route path={RouteList.ORDER.link} element={<Order />} />
            <Route path={RouteList.PRODUCTS.link} element={<Products />} />
            <Route
              path={RouteList.PRODUCT_SELECTORS.link}
              element={<SelectorTemplates />}
            />
            <Route
              path={RouteList.PRODUCT_SELECTOR.link}
              element={<SelectorTemplateEditor />}
            />
            <Route path={RouteList.PRODUCT.link} element={<Product />} />
            <Route path={RouteList.DASHBOARD.link} element={<Dashboard />} />
            <Route path={RouteList.ENQUIRIES.link} element={<Enquiries />} />
            <Route path={RouteList.ENQUIRY.link} element={<Enquiry />} />
            <Route path={RouteList.STAGING.link} element={<Staging />} />
            <Route path={RouteList.SETTINGS.link} element={<Settings />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default App;
